// react
import { type ReactNode } from 'react';

// material ui
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';

// shared components
import { reactPlugin } from '@/components/application-insights/ApplicationInsightsService';
import { GlobalAlertProvider } from '@/components/ui/alert/GlobalAlertProvider';

// relative
import ContainerI18nWrapper from '../i18n/I18nWrapper';
import getTheme from '../theme/theme';

import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

interface AppProviderProps {
  mode: 'light' | 'dark';
  children: ReactNode;
}

/**
 * Merges the most global, app-wide providers (theme, telemetry,
 * date localization, translations and global alerts) into a single
 * component so `__root.tsx` doesn't need to nest them individually.
 */
export default function AppProvider({ mode, children }: AppProviderProps) {
  return (
    <ThemeProvider theme={getTheme(mode)}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <LocalizationProvider
          dateAdapter={AdapterLuxon}
          adapterLocale="da"
          localeText={
            daDK.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <ContainerI18nWrapper>
            <CssBaseline />
            <GlobalAlertProvider>{children}</GlobalAlertProvider>
          </ContainerI18nWrapper>
        </LocalizationProvider>
      </AppInsightsContext.Provider>
    </ThemeProvider>
  );
}
